"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import BrandLogo from "@/components/BrandLogo";
import TrackingLink from "@/components/landing/TrackingLink";
import DarkModeToggle from "@/components/DarkModeToggle";
import { Menu, X } from "lucide-react";
import { isPublicSignupOpen } from "@/lib/signup-access";

// ── Types ────────────────────────────────────────────────────

interface NavLink {
  href: string;
  label: string;
  highlight?: string; // optional Tailwind color class override
}

type AccentColor = "teal" | "lime" | "amber" | "blue" | "indigo" | "orange";

interface PublicHeaderProps {
  variant: "full" | "minimal" | "industry" | "legal";
  links?: NavLink[];
  accentColor?: AccentColor;
  showBanner?: boolean;
  ctaOverride?: { href: string; label: string };
  sticky?: boolean;
  maxWidth?: string;
}

// ── Accent color map for industry CTA buttons ────────────────

const accentStyles: Record<AccentColor, string> = {
  teal: "bg-teal-600 hover:bg-teal-500 shadow-teal-900/30",
  lime: "bg-lime-600 hover:bg-lime-500 shadow-lime-900/30",
  amber: "bg-amber-600 hover:bg-amber-500 shadow-amber-900/30",
  blue: "bg-blue-600 hover:bg-blue-500 shadow-blue-900/30",
  indigo: "bg-indigo-600 hover:bg-indigo-500 shadow-indigo-900/30",
  orange: "bg-orange-600 hover:bg-orange-500 shadow-orange-900/30",
};

// ── Full nav links (homepage) ────────────────────────────────

const FULL_NAV_LINKS: NavLink[] = [
  { href: "#features", label: "Features" },
  { href: "/pricing", label: "Pricing" },
  { href: "/roi-calculator", label: "ROI Calculator" },
  { href: "/enterprise", label: "Enterprise" },
  { href: "/case-studies", label: "Industries" },
  { href: "/compare", label: "Compare" },
  { href: "/contact", label: "Contact Us" },
];

// ── Industry nav links ───────────────────────────────────────

const INDUSTRY_NAV_LINKS: NavLink[] = [
  { href: "/#how-it-works", label: "How It Works" },
  { href: "/roi-calculator", label: "ROI Calculator" },
];

// ── Component ────────────────────────────────────────────────

export default function PublicHeader({
  variant,
  links,
  accentColor = "blue",
  showBanner = false,
  ctaOverride,
  sticky = false,
  maxWidth = "max-w-6xl",
}: PublicHeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const desktopLogoSize = variant === "full" ? 44 : 42;
  const mobileLogoSize = 32;

  const isDark = true;
  const isFixed = variant === "full";
  const isSticky = sticky && !isFixed;

  // Resolve nav links based on variant
  const navLinks =
    links ??
    (variant === "full"
      ? FULL_NAV_LINKS
      : variant === "industry"
        ? INDUSTRY_NAV_LINKS
        : []);

  // Close mobile menu on Escape; lock scroll when open
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen]);

  // Position classes
  const positionClass = isFixed
    ? "fixed top-0 w-full z-50"
    : isSticky
      ? "sticky top-0 z-50"
      : "";

  // Background classes — refined, minimal, premium
  const bgClass = "bg-navy/95 border-b border-white/10 backdrop-blur-2xl";

  // Text classes for links — higher contrast, refined hover
  const linkClass = isDark
    ? "text-gray-400 hover:text-white transition-colors duration-150"
    : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-150";

  // CTA button
  const ctaHref = ctaOverride?.href ?? "/signup";
  const ctaLabel = ctaOverride?.label ?? (isPublicSignupOpen() ? "Start Free Trial" : "Request Access");

  return (
    <>
      <nav className={`${positionClass} ${bgClass}`} aria-label="Main navigation">
        <div className={`max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between`}>
          {/* Logo — v2 horizontal lockup (symbol + ADITYA LABS wordmark in Inter Medium white) */}
          <BrandLogo size={desktopLogoSize} showName className="shrink-0" />

          {/* Center nav links (desktop only) */}
          {navLinks.length > 0 && (
            <div className={`hidden lg:flex flex-1 items-center justify-center gap-1 text-[15px] ${isDark ? "font-medium" : ""}`}>
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`px-3 py-2.5 rounded-lg ${link.highlight ?? linkClass} hover:bg-white/[0.05]`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}

          {/* Right side */}
          <div className="flex items-center gap-3 shrink-0">
            {variant === "legal" ? (
              <>
                <DarkModeToggle />
                <Link
                  href="/"
                  className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition font-medium"
                >
                  Back to Home
                </Link>
              </>
            ) : (
              <>
                <DarkModeToggle />

                {/* Desktop: Sign in link */}
                <Link
                  href="/login"
                  className={`hidden sm:inline-flex text-[15px] font-medium px-3 py-2.5 rounded-lg transition-colors duration-150 ${
                    isDark
                      ? "text-gray-400 hover:text-white hover:bg-white/[0.05]"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  Sign In
                </Link>

                {/* CTA button — premium pill style */}
                {variant === "full" ? (
                  <TrackingLink
                    href={ctaHref}
                    eventLabel="nav_start_free"
                    className="hidden sm:inline-flex relative bg-gradient-to-r from-white to-gray-100 text-gray-900 text-[15px] font-semibold px-6 py-2.5 rounded-full transition-all duration-200 shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:shadow-[0_0_25px_rgba(255,255,255,0.2)] hover:scale-[1.03] active:scale-[0.98] ring-1 ring-white/20"
                  >
                    {ctaLabel}
                  </TrackingLink>
                ) : variant === "industry" ? (
                  <Link
                    href={ctaHref}
                    className={`hidden sm:inline-flex ${accentStyles[accentColor]} text-white px-5 py-2.5 rounded-full text-[15px] font-semibold transition-all shadow-lg`}
                  >
                    {ctaLabel}
                  </Link>
                ) : (
                  <Link
                    href={ctaHref}
                    className="hidden sm:inline-flex bg-blue-600 hover:bg-blue-700 text-white text-[15px] font-medium px-5 py-2.5 rounded-full transition"
                  >
                    {ctaLabel}
                  </Link>
                )}

                {/* Mobile hamburger — visible below lg */}
                {navLinks.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setMobileOpen(true)}
                    aria-label="Open navigation menu"
                    aria-expanded={mobileOpen}
                    aria-controls="mobile-nav-drawer"
                    className={`lg:hidden inline-flex items-center justify-center w-12 h-12 rounded-2xl transition-colors ${
                      isDark
                        ? "text-white hover:bg-white/[0.08]"
                        : "text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile nav drawer — lg:hidden, slides in from right */}
      {navLinks.length > 0 && variant !== "legal" && (
        <>
          {/* Backdrop */}
          <div
            className={`lg:hidden fixed inset-0 z-[60] bg-navy/80 backdrop-blur-sm transition-opacity duration-200 ${
              mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer */}
          <aside
            id="mobile-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className={`lg:hidden fixed top-0 right-0 bottom-0 z-[61] w-[85%] max-w-sm bg-navy/95 shadow-2xl transform transition-transform duration-200 ease-out ${
              mobileOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <BrandLogo size={mobileLogoSize} showName className="shrink-0" />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation menu"
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer links */}
            <nav className="px-4 py-4 flex flex-col gap-1" aria-label="Mobile navigation">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-3 rounded-lg text-[15px] font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Drawer footer — Sign in + CTA */}
            <div className="absolute bottom-0 left-0 right-0 px-4 py-4 border-t border-gray-200 dark:border-gray-800 flex flex-col gap-2">
              <Link
                href="/login"
                onClick={() => setMobileOpen(false)}
                className="w-full text-center px-4 py-2.5 rounded-full text-[14px] font-medium text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                Sign In
              </Link>
              <Link
                href={ctaHref}
                onClick={() => setMobileOpen(false)}
                className="w-full text-center bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold px-4 py-2.5 rounded-full transition"
              >
                {ctaLabel}
              </Link>
            </div>
          </aside>
        </>
      )}

      {/* Promotional banner (homepage full variant only) */}
      {variant === "full" && showBanner && (
        <div className="fixed top-[73px] w-full z-40 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 text-white py-2 px-4">
          <div className={`${maxWidth} mx-auto text-center text-sm font-medium flex items-center justify-center gap-3 flex-wrap`}>
            <span>
              <strong>Private Preview</strong> &mdash; guided onboarding is available by request
            </span>
            <a href="/signup" className="bg-white text-purple-600 font-bold text-xs px-3 py-1 rounded-full hover:bg-gray-100 transition">
              {isPublicSignupOpen() ? "Start Free Trial" : "Request Access"} &rarr;
            </a>
          </div>
        </div>
      )}
    </>
  );
}
